import React from "react"

import DayJsUtils from "@date-io/dayjs"
import { Global, ThemeProvider } from "@emotion/react"
import { StylesProvider, ThemeProvider as MuiThemeProvider } from "@material-ui/core"
import { MuiPickersUtilsProvider } from "@material-ui/pickers"
import { HelmetProvider } from "react-helmet-async"
import { BrowserRouter as Router } from "react-router-dom"

import { ClientVersionHeader, NcsApiProvider } from "@ncs/ncs-api"
import { ToastProvider, UrlStateProvider } from "@ncs/web-legos"
import { CrashErrorPage } from "~/shared-components"

import { fonts, globalStyles, reset, theme } from "~/styles"
import * as Sentry from "@sentry/react"
import { Integrations } from "@sentry/tracing"
import { ShopContextProvider } from "./ShopContext"

declare global {
	interface Window {
		sentry?: any
		__SENTRY__?: any
	}
}
Sentry.init({
	enabled: !!import.meta.env.VITE_SENTRY_DSN,
	dsn: import.meta.env.VITE_SENTRY_DSN,
	integrations: [new Integrations.BrowserTracing()],
	tracesSampleRate: 0.1,
	release: import.meta.env.VITE_RELEASE_VERSION,
})

// eslint-disable-next-line no-console
console.log(`Sentry Release Version : ${import.meta.env.VITE_RELEASE_VERSION}`)

window.sentry = { ...window.__SENTRY__ }

export const AppProvider: React.FC = ({ children }) => (
	<HelmetProvider>
		<NcsApiProvider clientVersionHeader={ClientVersionHeader.Customer}>
			<MuiThemeProvider theme={theme}>
				<ThemeProvider theme={theme}>
					<MuiPickersUtilsProvider utils={DayJsUtils}>
						<Global styles={reset} />
						<Global styles={globalStyles} />
						<StylesProvider
							injectFirst // injectFirst lets us override MUI with emotion CSS
						>
							<Sentry.ErrorBoundary fallback={<CrashErrorPage />}>
								<Router>
									<UrlStateProvider>
										<ToastProvider>
											<ShopContextProvider>{children}</ShopContextProvider>
										</ToastProvider>
									</UrlStateProvider>
								</Router>
							</Sentry.ErrorBoundary>
						</StylesProvider>
						<Global styles={fonts} />
					</MuiPickersUtilsProvider>
				</ThemeProvider>
			</MuiThemeProvider>
		</NcsApiProvider>
	</HelmetProvider>
)
